/**
 * Categorias de erro usadas no bootstrap do login (validação de OTP e
 * carregamento da sessão). A classificação permite que a UI diferencie
 * "código inválido" de "falha ao carregar a sessão" e que a telemetria
 * registre a natureza do erro sem expor dados sensíveis.
 */
export type SessionErrorKind =
  | 'canceled'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'no_http_response'
  | 'retryable_http'
  | 'http_error'
  | 'invalid_token'
  | 'unexpected';

interface AxiosLikeError {
  isAxiosError?: boolean;
  code?: string;
  response?: { status?: number };
}

/**
 * Detecta um erro no formato do Axios sem depender de `axios.isAxiosError`,
 * que pode não estar disponível quando o módulo `axios` está mockado.
 */
const asAxiosError = (error: unknown): AxiosLikeError | null => {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const candidate = error as AxiosLikeError;
  if (candidate.isAxiosError === true || 'response' in candidate || 'code' in candidate) {
    return candidate;
  }
  return null;
};

export const classifySessionError = (error: unknown): SessionErrorKind => {
  if (error instanceof Error && error?.message === 'INVALID_OR_EXPIRED_SESSION_TOKEN') {
    return 'invalid_token'
  }

  const axiosError = asAxiosError(error);

  if (!axiosError) {
    return 'unexpected';
  }

  if (axiosError.code === 'ERR_CANCELED') {
    return 'canceled';
  }

  if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
    return 'timeout';
  }

  const status = axiosError.response?.status;

  if (status === 401) {
    return 'unauthorized';
  }

  if (status === 403) {
    return 'forbidden';
  }

  if (!status) {
    return 'no_http_response';
  }

  if (status === 408 || status === 429 || status >= 500) {
    return 'retryable_http';
  }

  return 'http_error';
};
